import { createContext, useContext } from 'react';
import * as RoutesModule from 'Routes';
import { App } from './App';
import registerServiceWorker from './registerServiceWorker';
import { AxiosInstance } from 'axios';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import i18n from 'i18next';
import translationsEn from './locales/en/translation-en.json';
import { createRoot } from 'react-dom/client';

// services
import { ConfigurationService } from 'services/ConfigurationService';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';

import { t as _t } from 'i18next';

// stores
import { LocationStore } from 'stores/LocationStore';
import { LocationFinderApiService } from 'stores/LocationFinderApiService';
import { NotificationsStore } from 'stores/NotificationsStore';
import StartupService from 'services/StartupService';
import StartupStore from 'stores/StartupStore';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

registerServiceWorker();

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  locationFinderApiService: LocationFinderApiService;
  locationStore: LocationStore;
  startupStore: StartupStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

const langResources = {
  en: { translation: translationsEn },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : '';
};

export const t = (key: string) => _t(key) as string;

async function renderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance();

  const locationFinderApiService = new LocationFinderApiService();
  const notificationStore = new NotificationsStore();
  const locationStore = new LocationStore(locationFinderApiService, notificationStore);

  const startupService = new StartupService(apiServiceAxiosInstance);
  const startupStore = new StartupStore(authenticationService, configurationService, startupService, notificationStore);

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    locationFinderApiService,
    locationStore,
    startupStore,
  };

  await i18n.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(navigator.language.slice(0, 2)) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance, authenticationService, errorHandler);

  root.render(
    <BrowserRouter>
      <StoresContext.Provider value={stores}>
        <App>{RoutesModule.routes}</App>
      </StoresContext.Provider>
    </BrowserRouter>
  );
}

renderApp();
